import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiResponse } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Observable, catchError, debounceTime, finalize, map, of, startWith, switchMap } from 'rxjs';
import _ from 'lodash';
import { CostCenter } from '../../../models/cost-center/cost-center';
import { ExpenseConcept } from '../../../models/expense-concept/expense-concept';
import { InventoryType } from '../../../models/inventory-type/inventory-type';
import {
  GlAccountRule,
  GlAccountRulePreview,
  GlAccountRulePreviewCommand,
  GlAccountRuleRole,
  GlAccountRuleScope,
  glAccountRuleRolesByScope,
} from '../../../models/gl-account-rule/gl-account-rule';
import { GlAccountRuleFormModel, glAccountRuleFieldLabels } from '../../../models/gl-account-rule/gl-account-rule-form';
import { GlAccountRuleProxyService } from '../../../proxies/gl-account-rule.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { ControlsOf } from '../../../utilities/form.types';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';

/** What the page asks the drawer to open: an existing rule, or a new one pre-filled from the current view. */
export interface GlAccountRuleDrawerRequest {
  rule: GlAccountRule | null;
  defaults: {
    scope: GlAccountRuleScope;
    role: GlAccountRuleRole;
    costCenterCode: string | null;
    conceptCode: string | null;
    inventoryTypeReference: string | null;
  } | null;
}

interface Option<T> {
  label: string;
  value: T;
}

/** Null = the key isn't complete yet, so there is nothing to preview. */
type PreviewState = { loading: boolean; preview: GlAccountRulePreview | null };

/**
 * New / edit GL account rule (#5784). Scope, role and the three dimensions are the rule's identity, so an
 * existing rule only changes its account or is deactivated. The preview comes from the API's resolver
 * (POST gl-account-rule/preview) as the key changes, so it always agrees with what posting will do.
 */
@Component({
  selector: 'app-gl-account-rule-drawer',
  templateUrl: './gl-account-rule-drawer.component.html',
  styleUrl: './gl-account-rule-drawer.component.scss',
})
export class GlAccountRuleDrawerComponent implements OnInit {
  @Input({ required: true }) request!: GlAccountRuleDrawerRequest;
  @Input({ required: true }) financialCompanyReference!: string;
  @Input({ required: true }) costCenters: CostCenter[] = [];
  @Input({ required: true }) concepts: ExpenseConcept[] = [];
  @Input({ required: true }) inventoryTypes: InventoryType[] = [];
  @Input() canEdit = false;

  /** True when a rule was saved or deactivated, so the page reloads. */
  @Output() closed = new EventEmitter<boolean>();

  private readonly proxy = inject(GlAccountRuleProxyService);
  private readonly translate = inject(TranslateService);
  private readonly locale = inject(LocaleHelperService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly Scope = GlAccountRuleScope;
  readonly fieldLabels = glAccountRuleFieldLabels;

  visible = true;
  /** Set once a save closes the drawer, so the hide that follows doesn't report "nothing changed". */
  private saved = false;
  readonly isSaving = signal(false);
  readonly previewState = signal<PreviewState>({ loading: false, preview: null });

  readonly form = new FormGroup<ControlsOf<GlAccountRuleFormModel>>({
    scope: new FormControl(GlAccountRuleScope.ExpenseClaim, { nonNullable: true, validators: [Validators.required] }),
    role: new FormControl(GlAccountRuleRole.Expense, { nonNullable: true, validators: [Validators.required] }),
    costCenterCode: new FormControl('', { nonNullable: true }),
    conceptCode: new FormControl('', { nonNullable: true }),
    inventoryTypeReference: new FormControl('', { nonNullable: true }),
    glAccountReference: new FormControl<string | null>(null, { validators: [Validators.required] }),
  });

  readonly scope = signal(GlAccountRuleScope.ExpenseClaim);

  readonly isEdit = computed(() => !_.isNil(this.request?.rule));

  readonly scopeOptions = computed<Option<GlAccountRuleScope>[]>(() => [
    { label: this.t('GlAccountRule_Scope_ExpenseClaim', 'Expense claim'), value: GlAccountRuleScope.ExpenseClaim },
    { label: this.t('GlAccountRule_Scope_InventoryMovement', 'Inventory movement'), value: GlAccountRuleScope.InventoryMovement },
  ]);

  readonly roleOptions = computed<Option<GlAccountRuleRole>[]>(() =>
    glAccountRuleRolesByScope[this.scope()].map((role) => ({ label: this.roleLabel(role), value: role })),
  );

  costCenterOptions: Option<string>[] = [];
  conceptOptions: Option<string>[] = [];
  inventoryTypeOptions: Option<string>[] = [];

  ngOnInit(): void {
    this.buildOptions();
    this.patchFromRequest();

    this.form.controls.scope.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((scope) => this.onScopeChange(scope));

    // Only the key drives the preview; the account doesn't change which cost centers a rule wins.
    this.form.valueChanges
      .pipe(
        startWith(this.form.getRawValue()),
        map(() => this.previewCommand()),
        debounceTime(300),
        switchMap((command) => this.fetchPreview(command)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.previewState.set(state));
  }

  private buildOptions(): void {
    const byCode = new Map(this.costCenters.map((c) => [c.code, c]));
    const depth = (code: string): number => {
      let d = 0;
      let parent = byCode.get(code)?.parentCode ?? null;
      while (!_.isNil(parent) && d < 32) {
        d++;
        parent = byCode.get(parent)?.parentCode ?? null;
      }
      return d;
    };
    this.costCenterOptions = [
      { label: this.t('GlAccountRule_AnyCostCenter_General', 'Any cost center (general)'), value: '' },
      ..._.sortBy(
        this.costCenters.filter((c) => c.isActive !== false),
        (c) => c.code,
      ).map((c) => ({
        label: `${'   '.repeat(depth(c.code))}${this.locale.getLocalizedValue(c.name) || c.code} · ${c.code}`,
        value: c.code,
      })),
    ];
    this.conceptOptions = this.concepts
      .filter((c) => c.isActive)
      .map((c) => ({ label: `${this.locale.getLocalizedValue(c.name) || c.code} · ${c.code}`, value: c.code }));
    this.inventoryTypeOptions = this.inventoryTypes.map((t) => ({ label: this.locale.getLocalizedValue(t.name) || t.id, value: t.id }));
  }

  private patchFromRequest(): void {
    const rule = this.request.rule;
    if (!_.isNil(rule)) {
      this.form.patchValue(
        {
          scope: rule.scope,
          role: rule.role,
          costCenterCode: rule.costCenterCode ?? '',
          conceptCode: rule.conceptCode ?? '',
          inventoryTypeReference: rule.inventoryTypeReference ?? '',
          glAccountReference: rule.glAccount.reference,
        },
        { emitEvent: false },
      );
      this.scope.set(rule.scope);
      // The dimensions are the rule's identity: the API refuses to change them, so they are read-only here.
      this.form.controls.scope.disable({ emitEvent: false });
      this.form.controls.role.disable({ emitEvent: false });
      this.form.controls.costCenterCode.disable({ emitEvent: false });
      this.form.controls.conceptCode.disable({ emitEvent: false });
      this.form.controls.inventoryTypeReference.disable({ emitEvent: false });
      if (!this.canEdit) {
        this.form.controls.glAccountReference.disable({ emitEvent: false });
      }
      return;
    }

    const defaults = this.request.defaults;
    if (_.isNil(defaults)) return;
    this.form.patchValue(
      {
        scope: defaults.scope,
        role: defaults.role,
        costCenterCode: defaults.costCenterCode ?? '',
        conceptCode: defaults.conceptCode ?? '',
        inventoryTypeReference: defaults.inventoryTypeReference ?? '',
      },
      { emitEvent: false },
    );
    this.scope.set(defaults.scope);
  }

  /** A rule carrying the other scope's dimension could never match a line, and each scope has its own roles. */
  private onScopeChange(scope: GlAccountRuleScope): void {
    this.scope.set(scope);
    const roles = glAccountRuleRolesByScope[scope];
    if (!roles.includes(this.form.controls.role.value)) {
      this.form.controls.role.setValue(roles[0]);
    }
    if (scope === GlAccountRuleScope.ExpenseClaim) {
      this.form.controls.inventoryTypeReference.setValue('');
    } else {
      this.form.controls.conceptCode.setValue('');
    }
  }

  private previewCommand(): GlAccountRulePreviewCommand {
    const value = this.form.getRawValue();
    return {
      ruleReference: this.request.rule?.id ?? null,
      scope: value.scope,
      role: value.role,
      financialCompanyReference: this.financialCompanyReference,
      costCenterCode: value.costCenterCode || null,
      conceptCode: value.scope === GlAccountRuleScope.ExpenseClaim ? value.conceptCode || null : null,
      inventoryTypeReference: value.scope === GlAccountRuleScope.InventoryMovement ? value.inventoryTypeReference || null : null,
    };
  }

  private fetchPreview(command: GlAccountRulePreviewCommand): Observable<PreviewState> {
    return this.proxy.preview(command).pipe(
      map((response) => ({ loading: false, preview: response.data ?? null })),
      catchError(() => of({ loading: false, preview: null })),
      startWith({ loading: true, preview: this.previewState().preview }),
    );
  }

  // ---- preview display ----

  winningNames(preview: GlAccountRulePreview): string[] {
    return preview.costCenterCodes.map((code) => this.costCenterName(code));
  }

  replacementText(preview: GlAccountRulePreview): string[] {
    return preview.replaces.map((r) =>
      this.t('GlAccountRule_Preview_ReplaceLine', '{account} (from {from}) in {where}')
        .replace('{account}', r.glAccount.number ?? '—')
        .replace(
          '{from}',
          _.isNil(r.ruleCostCenterCode)
            ? this.t('GlAccountRule_AnyCostCenter', 'Any cost center')
            : this.costCenterName(r.ruleCostCenterCode),
        )
        .replace('{where}', r.costCenterCodes.map((code) => this.costCenterName(code)).join(', ')),
    );
  }

  conflictText(preview: GlAccountRulePreview): string {
    return this.t(
      'GlAccountRule_Preview_Conflict',
      'An active rule already has this combination ({account}). Edit that rule instead of creating another.',
    ).replace('{account}', preview.conflict?.glAccount.number ?? '—');
  }

  private costCenterName(code: string): string {
    const center = this.costCenters.find((c) => c.code === code);
    return _.isNil(center) ? code : this.locale.getLocalizedValue(center.name) || code;
  }

  // ---- actions ----

  onSave(): void {
    if (!this.canEdit) return;
    const value = this.form.getRawValue();
    // Stricter than the API, which reads a missing concept / inventory type as "any" (F-15): a catch-all
    // rule per scope is rare enough that the screen asks for the dimension rather than create one by accident.
    const missingDimension =
      (value.scope === GlAccountRuleScope.ExpenseClaim && !value.conceptCode) ||
      (value.scope === GlAccountRuleScope.InventoryMovement && !value.inventoryTypeReference);
    const account = value.glAccountReference;
    if (this.form.invalid || missingDimension || _.isNil(account)) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      if (missingDimension) {
        GlobalMessageUtil.warn(
          this.t('GlAccountRule_Title', 'GL Account Rules'),
          value.scope === GlAccountRuleScope.ExpenseClaim
            ? this.t('GlAccountRule_Require_Concept', 'Choose the concept.')
            : this.t('GlAccountRule_Require_InventoryType', 'Choose the inventory type.'),
        );
      }
      return;
    }

    const existing = this.request.rule;
    const request: Observable<ApiResponse<GlAccountRule>> = _.isNil(existing)
      ? this.proxy.create({
          scope: value.scope,
          role: value.role,
          financialCompanyReference: this.financialCompanyReference,
          costCenterCode: value.costCenterCode || null,
          conceptCode: value.scope === GlAccountRuleScope.ExpenseClaim ? value.conceptCode : null,
          inventoryTypeReference: value.scope === GlAccountRuleScope.InventoryMovement ? value.inventoryTypeReference : null,
          glAccount: { reference: account },
          isActive: true,
        })
      : this.proxy.update(existing.id, { glAccountReference: account, isActive: true });
    this.submit(request, this.t('GlAccountRule_Saved', 'GL account rule saved.'));
  }

  onDeactivate(): void {
    const existing = this.request.rule;
    if (!this.canEdit || _.isNil(existing)) return;
    this.confirmation.confirm({
      header: this.t('GlAccountRule_Deactivate_Title', 'Deactivate rule'),
      message: this.t(
        'GlAccountRule_Deactivate_Message',
        'Lines that took this rule will fall back to the next most specific rule, or to none. Continue?',
      ),
      acceptLabel: this.t('GlAccountRule_Deactivate', 'Deactivate'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      accept: () =>
        this.submit(
          this.proxy.update(existing.id, { glAccountReference: existing.glAccount.reference ?? '', isActive: false }),
          this.t('GlAccountRule_Deactivated', 'GL account rule deactivated.'),
        ),
    });
  }

  private submit(request: Observable<ApiResponse<GlAccountRule>>, message: string): void {
    this.isSaving.set(true);
    request.pipe(finalize(() => this.isSaving.set(false))).subscribe({
      next: (response) => {
        if (!response.success) return;
        GlobalMessageUtil.success(this.t('GlAccountRule_Title', 'GL Account Rules'), message);
        this.saved = true;
        this.visible = false;
        this.closed.emit(true);
      },
      // The error interceptor already shows the API's message; staying open lets the user correct it.
      error: () => undefined,
    });
  }

  onHide(): void {
    if (this.saved) return;
    this.closed.emit(false);
  }

  private roleLabel(role: GlAccountRuleRole): string {
    switch (role) {
      case GlAccountRuleRole.Expense:
        return this.t('GlAccountRule_Role_Expense', 'Expense');
      case GlAccountRuleRole.Inventory:
        return this.t('GlAccountRule_Role_Inventory', 'Inventory');
      case GlAccountRuleRole.Offset:
        return this.t('GlAccountRule_Role_Offset', 'Offset');
      default:
        return this.t('GlAccountRule_Role_Tax', 'Tax');
    }
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
